import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, Clock, Footprints, CalendarDays, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { kmToMiles } from "@/lib/time";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export default function WeekSummary({ classes, onPickDay, userLoc, today, rain, accessible, pref }) {
  const [sum, setSum] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (userLoc) { q.set("lat", userLoc[0]); q.set("lng", userLoc[1]); }
    if (today) q.set("today", today);
    if (rain) q.set("rain", "true");
    if (accessible) q.set("accessible", "true");
    if (pref) q.set("pref", pref);
    api.get(`/week/summary?${q.toString()}`).then((r) => setSum(r.data)).catch(() => {});
  }, [classes, userLoc, today, rain, accessible, pref]);

  if (!sum) return null;
  const maxLoad = Math.max(1, ...sum.per_day.map((d) => d.class_min));

  const stats = [
    { icon: BookOpen, label: "Courses", value: sum.unique_courses },
    { icon: CalendarDays, label: "Days on campus", value: sum.days_on_campus },
    { icon: Clock, label: "Class hours/wk", value: sum.total_class_hours },
    { icon: Footprints, label: "Walking/wk", value: `${kmToMiles(sum.total_walk_km).toFixed(1)} mi` },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5 rounded-xl border border-border bg-white overflow-hidden"
      data-testid="week-summary"
    >
      <div className="p-5 lg:p-6">
        <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-2">
          <Sparkles size={12} strokeWidth={1.5} /> Your week at a glance
        </div>
        <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-snug" data-testid="week-headline">
          {sum.headline}
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <s.icon size={14} strokeWidth={1.5} />
                <span className="text-[11px] font-semibold uppercase tracking-wide">{s.label}</span>
              </div>
              <div className="font-display text-2xl font-semibold mono">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Per-day load bars */}
        <div className="flex items-center justify-between mt-5 mb-1.5">
          <span className="mono text-[11px] tracking-widest uppercase text-muted-foreground">Daily class load</span>
          <span className="mono text-[11px] tracking-widest uppercase text-brand">↑ leave by · <span className="text-[#16A34A]">• live</span></span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {sum.per_day.map((d) => {
            const busiest = d.day === sum.busiest_day;
            const free = d.day === sum.most_free_day && d.biggest_gap >= 45;
            return (
              <button
                key={d.day}
                data-testid={`week-day-${d.day}`}
                onClick={() => onPickDay?.(d.day)}
                className="group text-left"
              >
                <div className="h-20 flex items-end rounded-md bg-secondary/60 overflow-hidden">
                  <div
                    className={`w-full transition-all ${busiest ? "bg-brand" : "bg-brand/45 group-hover:bg-brand/70"}`}
                    style={{ height: `${d.on_campus ? Math.max(12, (d.class_min / maxLoad) * 100) : 0}%` }}
                  />
                </div>
                <div className="mt-1.5 mono text-xs font-bold text-center">{d.day}</div>
                <div className="text-[11px] text-center text-muted-foreground">
                  {d.on_campus ? `${d.classes} cls` : "free"}
                  {d.alerts > 0 && <span className="text-destructive"> · !</span>}
                  {free && <span className="text-[#166534]"> · gap</span>}
                </div>
                {d.on_campus && d.leave_by && (
                  <div className={`mono text-[11px] text-center font-bold ${d.live_origin ? "text-[#16A34A]" : "text-brand"}`} title={d.live_origin ? "Leave by (from your live location)" : "Leave by"}>
                    ↑ {d.leave_by}{d.live_origin && <span className="ml-0.5">•</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {sum.tips.length > 0 && (
          <div className="mt-5 space-y-2">
            {sum.tips.map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Lightbulb size={15} strokeWidth={1.5} className="text-[#F59E0B] shrink-0 mt-0.5" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
